import type {
  NewClientsListFilters,
  Segment,
} from '@pages/Clients/NewClientsList/types';

type PrimitiveArrayValue = string | number | boolean;

const normalizeArray = (
  values: ReadonlyArray<PrimitiveArrayValue> | null | undefined
): string[] => {
  if (!values?.length) return [];

  return values
    .map((value) => `${typeof value}:${String(value)}`)
    .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue));
};

const normalizeNumberRange = (
  range: { from: number | null; to: number | null } | null | undefined
): { from: number | null; to: number | null } => ({
  from: range?.from ?? null,
  to: range?.to ?? null,
});

const normalizeDateRange = (
  range: NewClientsListFilters['period']
): [number | null, number | null] | null => {
  if (!range) return null;

  const [from, to] = range;

  return [from?.valueOf() ?? null, to?.valueOf() ?? null];
};

const createFilterFingerprint = (filter: NewClientsListFilters): string => {
  return JSON.stringify({
    type: filter.type,

    hasRecords: filter.hasRecords ?? null,
    hasTasks: filter.hasTasks ?? null,
    hasLoyaltyCard: filter.hasLoyaltyCard ?? null,

    recordsStatuses: normalizeArray(filter.recordsStatuses),
    recordsCountRange: normalizeNumberRange(filter.recordsCountRange),
    servicesSumRange: normalizeNumberRange(filter.servicesSumRange),

    recordsTags: normalizeArray(filter.recordsTags),
    isIncludeRecordsTags: filter.isIncludeRecordsTags,

    period: normalizeDateRange(filter.period),

    employees: normalizeArray(filter.employees),
    categories: normalizeArray(filter.categories),
    services: normalizeArray(filter.services),
    genders: normalizeArray(filter.genders),

    clientsTags: normalizeArray(filter.clientsTags),
    isIncludeClientsTags: filter.isIncludeClientsTags,

    soldRange: normalizeNumberRange(filter.soldRange),
    birthdayPeriod: normalizeDateRange(filter.birthdayPeriod),
    ageRange: normalizeNumberRange(filter.ageRange),
    balanceRange: normalizeNumberRange(filter.balanceRange),
  });
};

export const createFiltersFingerprint = (
  filters: NewClientsListFilters[]
): string => {
  return filters
    .map(createFilterFingerprint)
    .sort((firstFilter, secondFilter) =>
      firstFilter.localeCompare(secondFilter)
    )
    .join('|');
};

export const getSelectedSegmentValue = (
  currentFilters: NewClientsListFilters[],
  segments: Segment[]
): Segment['value'] | null => {
  const currentFiltersFingerprint = createFiltersFingerprint(currentFilters);

  const selectedSegment = segments.find((segment) => {
    return (
      createFiltersFingerprint(segment.filters) === currentFiltersFingerprint
    );
  });

  return selectedSegment?.value ?? null;
};
